/**
 * Sentry browser monitoring for the public Web site.
 *
 * Captured: unhandled errors + promise rejections, and Web-Vitals / page-load &
 * navigation performance (browser tracing). Events are tagged with
 * `environment` + `release` so the self-hosted dashboard (project id 6) can
 * split staging vs production.
 *
 * Session Replay is deliberately NOT enabled here: this is a content-oriented
 * marketing site whose top priority is fast first paint (Web/CLAUDE.md), and
 * the replay bundle is heavy. Add it later if the value outweighs the weight.
 *
 * Privacy: `sendDefaultPii: false` — no IP/cookies attached automatically.
 *
 * Disabled automatically on the local dev server (`import.meta.env.DEV`) so
 * developer machines never pollute the dashboard.
 */
import * as Sentry from '@sentry/react';

import { env } from './env';

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  // Never run from the local Vite dev server — only built (staging/prod) bundles.
  if (import.meta.env.DEV) return;

  const dsn = env.sentryDsn();
  if (!dsn) return;
  initialised = true;

  Sentry.init({
    dsn,
    environment: env.sentryEnvironment(),
    // When the Sentry Vite plugin uploads source maps it injects the release
    // automatically; this explicit value (CI git SHA) is a belt-and-braces
    // override so runtime + artifacts always line up.
    release: env.sentryRelease(),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: env.sentryTracesSampleRate(),
    sendDefaultPii: false,
  });
}
